import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'
import {
  computePosition,
  autoUpdate,
  offset,
  flip,
  shift
} from '@floating-ui/dom'

export default class extends Controller {
  static targets = ['trigger']

  static values = {
    active: { default: false, type: Boolean },
    clickOutsideEnabled: { default: true, type: Boolean },
    placement: { default: '', type: String },
    strategy: { default: 'absolute', type: String },
    offset: { default: 8, type: Number }
  }

  #popoutEl = null
  #cleanupAutoUpdate = null

  connect() {
    this.#popoutEl = this.element.querySelector(':scope > popout')

    if (this.clickOutsideEnabledValue) useClickOutside(this)

    if (this.activeValue && this.placementValue) {
      this.#popoutEl.dataset.active = ''
      this.#startPositioning()
    }
  }

  disconnect() {
    this.#stopPositioning()
  }

  clickOutside() {
    this.close()
  }

  close() {
    this.activeValue = false
  }

  toggle() {
    this.activeValue = !this.activeValue
  }

  activeValueChanged() {
    if (!this.placementValue || !this.#popoutEl) return

    if (this.activeValue) {
      this.#popoutEl.dataset.active = ''
      this.#startPositioning()
    } else {
      delete this.#popoutEl.dataset.active
      this.#stopPositioning()
    }
  }

  get #triggerEl() {
    if (this.hasTriggerTarget) return this.triggerTarget

    for (const child of this.element.children) {
      if (child !== this.#popoutEl) return child
    }

    return this.element
  }

  #startPositioning() {
    const popout = this.#popoutEl
    const trigger = this.#triggerEl
    if (!popout || !trigger) return

    this.#cleanupAutoUpdate = autoUpdate(trigger, popout, () => {
      computePosition(trigger, popout, {
        placement: this.placementValue,
        strategy: this.strategyValue,
        middleware: [offset(this.offsetValue), flip(), shift({ padding: 5 })]
      }).then(({ x, y }) => {
        Object.assign(popout.style, {
          left: `${x}px`,
          top: `${y}px`
        })
      })
    })
  }

  #stopPositioning() {
    this.#cleanupAutoUpdate?.()
    this.#cleanupAutoUpdate = null
  }
}
